import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, Store, MessagesSquare, Server, Activity, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { EARTH } from "@/pages/Landing";

const POINTS = [
  [Store, "Provider-Katalog", "Freie, lokale und kostenpflichtige KI-APIs – klar gekennzeichnet"],
  [MessagesSquare, "AI Playground", "Modelle testen und Routen nachvollziehen"],
  [Server, "Ollama & vLLM", "Lokale Modelle integrieren und sicher nutzen"],
  [Activity, "Monitoring & Limits", "Nutzung und Tageslimits im Blick"],
  [ShieldCheck, "Sicher & selbst gehostet", "Verschlüsselte Keys, keine Prompt-Speicherung"],
];

const Field = ({ icon: Icon, children }) => (
  <div className="relative"><Icon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />{children}</div>
);

export default function AuthPage({ initial = "login" }) {
  const [mode, setMode] = useState(initial);
  const [form, setForm] = useState({ email: "", password: "", name: "", remember: true });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setInfo("");
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setInfo(data.message);
      } else {
        const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", form);
        setUser(data);
        toast.success(mode === "login" ? "Willkommen zurück" : "Konto erstellt");
        nav("/app");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="auth-page">
      <section className="relative hidden overflow-hidden lg:block">
        <img src={EARTH} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060a18]/70 via-[#060a18]/60 to-[#060a18]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Logo sub size={44} />
          <div>
            <h1 className="text-5xl font-extrabold leading-tight text-white">Eine Plattform.<br /><span className="grad-text">Kostenlos zuerst.</span></h1>
            <div className="mt-10 space-y-5">
              {POINTS.map(([I, t, d]) => (
                <div key={t} className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-violet-600"><I className="h-5 w-5 text-white" /></div>
                  <div><div className="font-semibold text-white">{t}</div><div className="text-sm text-slate-400">{d}</div></div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-400">„Ein Hub. Alle KI-APIs. Für Entwickler, Teams und Visionäre.“ – Casi AI Labs</p>
        </div>
      </section>
      <section className="flex items-center justify-center p-6">
        <div className="glass glow w-full max-w-md p-8 fade-up">
          <div className="mb-6 flex justify-center"><Logo sub size={40} /></div>
          {mode !== "forgot" && (
            <div className="mb-6 grid grid-cols-2 rounded-lg border border-white/5 bg-black/20 p-1 text-sm">
              {[["login", "Anmelden"], ["register", "Registrieren"]].map(([m, l]) => (
                <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                  className={`rounded-md py-2 font-semibold transition-colors ${mode === m ? "bg-blue-500/20 text-blue-300 shadow-[inset_0_-2px_0_#4f86ff]" : "text-slate-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
          )}
          <h2 className="text-2xl font-bold text-white">{mode === "login" ? "Willkommen zurück" : mode === "register" ? "Konto erstellen" : "Passwort zurücksetzen"}</h2>
          <p className="mt-1 mb-6 text-sm text-slate-400">{mode === "forgot" ? "Wir erzeugen einen einmaligen Link (1 Stunde gültig)." : "Melde dich an, um deine KI-Umgebung zu nutzen."}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div><label className="label">Name</label>
                <Field icon={User}><input data-testid="auth-name-input" className="field pl-10 mt-1.5" value={form.name} onChange={set("name")} placeholder="Dein Name" /></Field></div>
            )}
            <div><label className="label">E-Mail-Adresse</label>
              <Field icon={Mail}><input data-testid="auth-email-input" type="email" required className="field pl-10 mt-1.5" value={form.email} onChange={set("email")} placeholder="deine@email.de" /></Field></div>
            {mode !== "forgot" && (
              <div>
                <div className="flex justify-between"><label className="label">Passwort</label>
                  {mode === "login" && <button type="button" data-testid="auth-forgot-link" onClick={() => setMode("forgot")} className="text-xs text-blue-400 hover:underline">Passwort vergessen?</button>}</div>
                <Field icon={Lock}>
                  <input data-testid="auth-password-input" type={show ? "text" : "password"} required minLength={mode === "register" ? 8 : 1} className="field pl-10 pr-10 mt-1.5" value={form.password} onChange={set("password")} placeholder={mode === "register" ? "Mindestens 8 Zeichen" : "Dein Passwort"} />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 mt-[3px] -translate-y-1/2 text-slate-500" aria-label="Passwort anzeigen" data-testid="auth-toggle-password">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </Field>
              </div>
            )}
            {mode === "login" && (
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" data-testid="auth-remember-checkbox" checked={form.remember} onChange={set("remember")} className="accent-blue-500" /> Angemeldet bleiben</label>
            )}
            {error && <div data-testid="auth-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}
            {info && <div data-testid="auth-info" className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm text-blue-200">{info}</div>}
            <button disabled={busy} data-testid="auth-submit-button" className="btn-grad flex w-full items-center justify-center gap-2 py-3">
              {busy ? "Bitte warten…" : mode === "login" ? "Anmelden" : mode === "register" ? "Registrieren" : "Reset-Link erzeugen"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-400">
            {mode === "login" ? <>Noch kein Konto? <button data-testid="auth-switch-register" onClick={() => setMode("register")} className="text-blue-400 hover:underline">Jetzt registrieren</button></>
              : <>Bereits registriert? <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="text-blue-400 hover:underline">Anmelden</button></>}
          </p>
        </div>
      </section>
    </div>
  );
}
